import React, { useEffect, useMemo } from 'react';
import { Form, Select, Switch } from 'antd';

interface SelectWithAllToggleProps {
	label: string;
	name: string; // field name, e.g. "problemTypeIds"
	allName: string; // toggle name, e.g. "allProblemTypes"
	form: any; // AntD Form instance
	defaultFormFieldStyle?: React.CSSProperties;
	children: React.ReactNode; // pass field from parent
	displaySelectAllWithLabel?: boolean;
	required?: boolean;
	requiredIfNotAllSelected?: Boolean;
	disabled?: boolean;
	selectAllPlaceholder?: string;
}

const SelectWithAllToggle: React.FC<SelectWithAllToggleProps> = ({
	label,
	name,
	allName,
	form,
	defaultFormFieldStyle = {},
	children,
	displaySelectAllWithLabel = false,
	required = false,
	requiredIfNotAllSelected = false,
	disabled = false,
	selectAllPlaceholder,
}) => {
	const selectedIds = Form.useWatch(name, form);
	const allSelected = Form.useWatch(allName, form);

	useEffect(() => {
		if (!selectAllPlaceholder) return;
		// Whenever "All" is on, the field must hold no specific ids. We never restore prior picks when
		// it is turned back off — choosing "All" discards them, so toggling off starts empty. (A rule
		// opened with specific ids stays "All" off, so this never runs and those ids remain visible.)
		if (allSelected) {
			form.setFieldsValue({ [name]: [] });
		}
	}, [allSelected, selectAllPlaceholder, form, name]);

	const allSelectedField = useMemo(
		() =>
			disabled ? null : (
				<div className="flex flex-row items-center">
					<span className="mr-2">Select All</span>
					<Form.Item name={allName} valuePropName="checked" noStyle>
						<Switch defaultChecked={false} size="small" />
					</Form.Item>
				</div>
			),
		[allName, disabled]
	);

	return (
		<Form.Item label={displaySelectAllWithLabel ? null : label} required={required}>
			<div className="flex flex-col" style={defaultFormFieldStyle}>
				{displaySelectAllWithLabel ? (
					<div className="mb-2 flex flex-row items-center justify-between">
						<span>{label}</span>
						{allSelectedField}
					</div>
				) : (
					<div className="mb-2 flex w-full flex-row items-center justify-between text-sm text-black/85">
						<div>{`Selected: ${allSelected ? 'All' : `(${(selectedIds || []).length})`}`}</div>
						{allSelectedField}
					</div>
				)}

				<Form.Item
					name={name}
					noStyle
					rules={[
						{
							type: 'array',
							required: requiredIfNotAllSelected && !allSelected,
							message: `Please select a ${label}`,
						},
					]}
				>
					{selectAllPlaceholder && allSelected ? (
						// "All" is on: render a plain disabled placeholder instead of the real field. The
						// stateful field component is unmounted entirely, so it can't retain a stale
						// selection. Toggling off remounts it fresh against the cleared form value.
						<Select
							disabled
							mode="multiple"
							placeholder={selectAllPlaceholder}
							className="w-full"
						/>
					) : React.isValidElement(children) ? (
						React.cloneElement(children, { disabled: allSelected || disabled })
					) : (
						children
					)}
				</Form.Item>
			</div>
		</Form.Item>
	);
};

export default SelectWithAllToggle;
